import React, {useState, useEffect, useMemo, useRef} from 'react';
import { useFormContext } from '../../Provider';
import useValidators from '../../Validators/useValidator';
import CommonFieldWrapper from '../CommonFieldWrapper';
import Icon from '../../../Icons/Icon';
import API from '../../../../Services/API';
import Loading from '../../../Loading';
import { ulid } from 'ulid';
import UploadManager from '../../../../Services/UploadManager';
import CircleBar from '../../../CircleBar';
import { useDialogues } from '../../../Dialogues';
import Library from './Library';
import { getAssets } from '../../../../Data/Assets';

const empty = [];
const uploads = new UploadManager();

const Assets = ({label, name, help, onChange, min, max, single, accept, dimensions, tags, previousTags, disabled, validators, showPrevious, showShared}) => {
    const { formData, updateField, updateValidity, isValidating, isSubmitting, formDisabled } = useFormContext();
    const value = useMemo(() => {
        const val = formData[name] ?? empty;
        return Array.isArray(val) ? val : [val];
    }, [formData, name]);
    const uploadBatch = useMemo(() => 'batch-' + ulid(), []);
    // const _min = single ? 1 : (min ?? 0);
    const _max = single ? 1 : (max ?? 999);
    const {isValid, error} = useValidators(value, validators, name, updateValidity, []);
    const fileField = useRef();
    const [assets, setAssets] = useState([]);
    const [removed, setRemoved] = useState([]);
    const [inProgress, setInProgress] = useState({});
    const {confirm} = useDialogues();
    const itemStyles = 'w-[75px] h-[75px] rounded-md flex items-center justify-center overflow-hidden cursor-pointer bg-cover shrink-0';
    const btnStyles = 'bg-gray-100 hover:bg-blue-100 hover:border hover:border-brand flex-col items-center justify-center text-xs text-gray-700 fill-gray-700';
    const [libraryOpen, setLibraryOpen] = useState(null);
    const [previous, setPrevious] = useState([]);
    const [shared, setShared] = useState([]);

    useEffect(() => {
        uploads.clearBatch(uploadBatch);
    }, [uploadBatch]);

    // Memoize tag strings to avoid unnecessary API calls
    const tagStr = useMemo(
        () => encodeURIComponent(tags.map(t => t.name + '=' + t.value).join('|')),
        [tags]
    );
    const previousTagStr = useMemo(
        () => encodeURIComponent([...tags, ...(previousTags ?? [])].map(t => t.name + '=' + t.value).join('|')),
        [tags, previousTags]
    );

    useEffect(() => {
        API.get('assets/shared?tags=' + tagStr).then(setShared)
        API.get('assets/previous?tags=' + previousTagStr).then(setPrevious)
    }, [tagStr, previousTagStr]);

    useEffect(() => {
        const assetIds = single ? [value] : (value ?? []);

        if (assetIds.length === 0) {
            setAssets([])
            return;
        }

        getAssets(assetIds).then(setAssets);
    }, [single, value]);

    const addAsset = (newAssetId, failedAssetId) => {
        setInProgress(ip => {
            const newIp = {...ip};
            delete newIp[newAssetId ?? failedAssetId];
            return newIp;
        });

        if (failedAssetId) {
            console.log('FAILED', failedAssetId)

        } else {
            updateField(name, (formVal) => {
                const newVal = single ? newAssetId : [newAssetId, ...formVal ?? []]
                onChange && onChange(newVal);
                return newVal;
            });
        }
    }

    const selectFile = (e) => {
        e.preventDefault();
        fileField.current.click();
    }

    const startUpload = (e) => {
        e.preventDefault();

        const count = e.target.files.length;
        for (let i = 0; i < count; i++) {
            const assetId = ulid();
            setInProgress(ip => ({...ip, [assetId]: 0}));

            uploads.newUpload(
                uploadBatch,
                e.target.files[i],
                assetId,
                tags,
                (progress) => setInProgress(ip => ({...ip, [assetId]: progress})),
                (asset, failed) => addAsset(asset.assetId, failed),
                dimensions
            )
        }
    }

    const removeAsset = async (assetId) => {
        setRemoved(r => [...r, assetId])
        const newVal = value.filter(v => v !== assetId);
        updateField(name, newVal);
        onChange && onChange(newVal);
    }

    const openLibrary = (e, type) => {
        e.preventDefault();
        setLibraryOpen(type)
    }

    const isDisabled = formDisabled || disabled || isSubmitting;
    return <CommonFieldWrapper {...{label, name, help, value, isValid, isValidating, error, validators, isDisabled}}>

        <div className="relative flex flex-row gap-2 flex-wrap">

            {/* Buttons */}

            <button className={`${itemStyles} ${btnStyles}`} onClick={selectFile} disabled={isDisabled}>
                <Icon icon="CloudArrowUp" size="2x" center />
                <p className="mt-1">Upload</p>
            </button>

            {(showPrevious && previous.length > 0) && <button className={`${itemStyles} ${btnStyles}`}
                onClick={(e) => openLibrary(e, 'previous')}
                disabled={isDisabled}
            >
                <Icon icon="FolderOpen" size="2x" center />
                <p className="mt-1">Mine</p>
            </button>}

            {(showShared && shared.length > 0) && <button className={`${itemStyles} ${btnStyles}`}
                onClick={(e) => openLibrary(e, 'shared')}
                disabled={isDisabled}
            >
                <Icon icon="FolderImage" size="2x" center />
                <p className="mt-1">Shared</p>
            </button>}

            {/* In progress */}

            {Object.keys(inProgress).map((pId, i) => {
                if (assets.find(a => a.id === pId) || removed.includes(pId)) {
                    return null;
                }
                const progress = inProgress[pId] ?? 0;
                return <div key={i} className={`${itemStyles} border`}>
                    {progress === 100 ? <Loading size="3x" center /> : <CircleBar size={50} percent={progress} />}
                </div>
            })}

            {/* Assets */}

            {Array.isArray(assets) && assets.map((asset, i) => {
                return <div key={i} className={`${itemStyles} relative border`} style={{backgroundImage: `url(${asset.thumbnail})`}}>
                    <div className="absolute top-0 right-0 w-5 h-5 rounded-full bg-red-500/75 hover:bg-red-500 flex items-center justify-center fill-white border border-white"
                        onClick={() => confirm(
                            {theme: 'danger', title: 'Remove file', message: 'Are you sure you want to remove this file?', confirmLabel: 'Remove'},
                            () => removeAsset(asset.id)
                        )}
                    >
                        <Icon icon="XMark" size="1x" center />
                    </div>
                    {/* <img src={asset.thumbnail} alt={asset.description} /> */}
                </div>
            })}

            {/* Loading */}

            {value.map(v => {
                if (assets.find(a => a.id === v) || removed.includes(v) || inProgress[v] !== undefined) {
                    return null;
                }
                return <div key={v} className={`${itemStyles} border border-dashed`}>
                    <Loading size="2x" center />
                </div>
            })}

        </div>

        <input ref={fileField}
            type="file"
            // capture={capture}
            onChange={startUpload}
            multiple={_max - assets.length}
            accept={accept || 'image/png, image/jpeg, image/jpg'}
            hidden
        />

        <Library {...{
            isOpen: libraryOpen,
            onClose: setLibraryOpen,
            title: libraryOpen === 'previous' ? 'Previous images' : 'Shared images',
            assets: libraryOpen === 'previous' ? previous : shared,
            onSelect: (a) => {
                addAsset(a);
                setLibraryOpen(null);
            },
            value
        }} />

    </CommonFieldWrapper>
}

export default Assets;
